import { getRuntimeEnvironmentFromProjectId } from '#shared/constants/firebase'
import { parseCookie } from '#shared/utils/cookies'
import { useApi } from '~/composables/api/useApi'

type ExponeaEventProperties = Record<string, any>

export function useExponea() {
    const { api } = useApi()
    const runtimeConfig = useRuntimeConfig()
    const nuxtApp = useNuxtApp()

    const getClientExponea = () => {
        if (!process.client) return null
        return nuxtApp.$exponea ?? window.$exponea ?? window.exponea ?? null
    }

    const getEventPropertiesWithEnvironment = (eventProperties: ExponeaEventProperties) => {
        const projectId = runtimeConfig.public.FIREBASE_PROJECT_ID || 'unknown'
        const growniEnvironment = getRuntimeEnvironmentFromProjectId(projectId)

        return {
            ...eventProperties,
            growni_environment: growniEnvironment,
            growni_project_id: projectId
        }
    }

    /**
     * Tracks an event in Exponea
     */
    async function trackExponeaEvent(data: {
        eventName: string
        userId?: string
        eventProperties: ExponeaEventProperties
        cookie?: string
    }) {
        if (process.client) {
            const eventProperties = getEventPropertiesWithEnvironment(data.eventProperties)

            console.log(`[Exponea] Tracking event: ${data.eventName}`, {
                userId: data.userId,
                ...eventProperties
            })

            const exponeaCookie = parseCookie(document.cookie)?.__exponea_etc__
            const exponea = getClientExponea()
            const normalizedUserId = typeof data.userId === 'string' ? data.userId.trim() : ''

            if (normalizedUserId) {
                const isTrackByEmail = normalizedUserId
                    .toLowerCase()
                    .match(
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    )
                const indentify = isTrackByEmail
                    ? { 'registered_email': normalizedUserId }
                    : { 'registered': normalizedUserId }

                if (typeof exponea?.identify === 'function') {
                    exponea.identify(indentify, {})
                }

                if (typeof exponea?.track === 'function') {
                    exponea.track(data.eventName, eventProperties)
                }

                // Also send via API if cookie exists (for server-side tracking)
                if (exponeaCookie) {
                    const exponeaData = { ...data, userId: normalizedUserId, eventProperties, cookie: exponeaCookie }
                    await api.post(`/exponea-track`, exponeaData)
                }
            } else {
                // Track exponea event when user is not logged in
                if (typeof exponea?.track === 'function') {
                    exponea.track(data.eventName, eventProperties)
                }
            }
        }
    }

    function track(eventName: string, eventProperties: ExponeaEventProperties = {}, userId?: string) {
        void trackExponeaEvent({ eventName, eventProperties, userId })
    }

    /**
     * Updates user data in Exponea
     */
    async function updateExponeaUser(data: any) {
        await api.post(`/exponea-update-user`, data)
    }

    return {
        track,
        trackExponeaEvent,
        updateExponeaUser
    }
}
